import cls from 'clsx';
import { useRouter } from 'next/router';
import React from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { STEPS } from '@/containers/CheckoutPage/constants';
import { getSelectedSubscription } from '@/containers/ConfigurePage/utils/getSelectedSubscription';
import { getCountryConfigFromLocale } from '@/lib/i18n/getCountryConfigFromLocale';
import { CityData } from '@/types/CityData';
import { TransformedAssetCategoryResult } from '@/types/TransformedAssetCategoryResults';
import { ProductSubscriptionByRegionResponse } from '@/utils/fetch/fetchProductSubscriptionByRegion';
import { isThreeTierProduct } from '@/utils/threeTier';
import { formValues, FormValues } from './formData';

interface Props {
  children: React.ReactNode;
  city: CityData;
  className?: string;
  isHeavyUseDisabled: boolean;
  product: TransformedAssetCategoryResult;
  subscriptions: ProductSubscriptionByRegionResponse;
}

interface ConnectFormProps {
  children: (props: Partial<FormValues>) => React.JSX.Element | null;
}

export const FormConnector = ({ children }: ConnectFormProps) => {
  const { control } = useFormContext<FormValues>();
  const currentFormValues = useWatch<FormValues>({ control });
  return children(currentFormValues);
};

export const FormConfig = ({
  children,
  className,
  isHeavyUseDisabled,
  product,
  subscriptions,
}: Props) => {
  const router = useRouter();

  const { countryCode } = getCountryConfigFromLocale(router.locale!);
  const isThreeTier = isThreeTierProduct(
    countryCode,
    product.assetCategoryCode
  );

  const methods = useForm<FormValues>({
    defaultValues: {
      durationMonths: formValues.durationMonths[isThreeTier ? '12' : '6'],
      student: formValues.student.no,
      heavyUse: isHeavyUseDisabled ? formValues.heavyUse.no : '',
    },
  });

  const onSubmit = (currentFormValues: FormValues) => {
    const currentSubscription = getSelectedSubscription({
      currentFormValues,
      product,
      subscriptions,
    });

    if (!currentSubscription) return;

    const urlObj = {
      pathname: '/[city]/[product]/checkout/[subscriptionCode]/[step]',
      query: {
        ...router.query,
        subscriptionCode: currentSubscription.code,
        step: [STEPS.PersonalDetails],
      },
    };

    router.push(urlObj);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={cls(className)}
      >
        {children}
      </form>
    </FormProvider>
  );
};
